import React, { useState, useEffect, useMemo } from "react";
import { FaPhoneAlt } from "react-icons/fa"; // Import React Icons
import { FaQuestion, FaCheck, FaFloppyDisk } from "react-icons/fa6";
import "../../css/Enquiries/EnquiriesList.css";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { db } from "./../../backend/firebaseConfig";
import Sidebar from "./../DesignSystem/Sidebar";
import Breadcrumb from "./../DesignSystem/Breadcrumb/Breadcrumb";
import "../../css/DesignSystem/Buttons/RadioButton.css";
import "./../../css/DesignSystem/Buttons/OldMembersHubButton.css";
import { Helmet } from "react-helmet-async";
import { useUserPermissions } from "../SignIn/UserPermissionsContext";
import { APP_ROUTES } from "../../constants/appRoutes";

const serviceColors = {
  "Mortgage Planning": "#E0DAFF", // Pastel Purple
  "Investment Planning": "#D5E4FA", // Pastel Blue
  "Wealth Management": "#FBD4F5", // Pastel Pink
  "Retirement Planning": "#FFFDC7", // Pastel Yellow
  "Insurance/ Protection": "#F0FDA1", // Pastel Green
  "Corporate Planning": "#FAD4D4", // Pastel Red
};

const NotesEditor = ({ value, onChange, onSave, saveState = "idle" }) => {
  const isSaving = saveState === "saving";
  const isSaved = saveState === "saved";
  const hasError = saveState === "error";

  return (
    <div className="enquiry-notes-editor">
      <textarea
        className="enquiry-notes-field Content-Small"
        rows={4}
        value={value}
        placeholder="Add an internal note..."
        aria-label="Notes and comments"
        onChange={(event) => onChange(event.target.value)}
      />
      <div className="enquiry-notes-actions">
        <span
          className={`enquiry-notes-feedback ${
            hasError ? "enquiry-notes-feedback--error" : ""
          }`}
          aria-live="polite"
        >
          {/* {isSaved
            ? "Notes saved"
            : hasError
              ? ""
              : ""} */}
        </span>
        <button
          type="button"
          className={`enquiry-notes-save ${
            isSaved ? "enquiry-notes-save--saved" : ""
          }`}
          onClick={onSave}
          disabled={isSaving}
        >
          {isSaved ? (
            <FaCheck aria-hidden="true" />
          ) : (
            <FaFloppyDisk aria-hidden="true" />
          )}
          {isSaving ? "Saving..." : isSaved ? "Saved" : "Save note"}
        </button>
      </div>
    </div>
  );
};

const EnquiriesList = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [brochureRequests, setBrochureRequests] = useState([]);
  const [filter, setFilter] = useState("all");
  const [check2Enquiries, setCheck2Enquiries] = useState([]);
  const [noteSaveStates, setNoteSaveStates] = useState({});
  const { userPermissions } = useUserPermissions();

  useEffect(() => {
    const fetchEnquiries = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "Enquiries"));
        const enquiriesData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          addressed: Boolean(doc.data().addressed),
        }));
        setEnquiries(enquiriesData);

        const querySnapshot1 = await getDocs(
          collection(db, "Enquiries", "Brochure Requests", "Requests"),
        );
        const brochureRequestsData = querySnapshot1.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBrochureRequests(brochureRequestsData);

        // NEW: Check² enquiries
        const check2Snap = await getDocs(
          collection(db, "Compliance", "Check²", "Enquiries"),
        );
        const check2Data = check2Snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setCheck2Enquiries(check2Data);
      } catch (error) {
        // console.error("Error fetching enquiries:", error);
      }
    };

    fetchEnquiries();
  }, []);

  const toggleAddressedStatus = async (id, currentStatus) => {
    try {
      const enquiryDoc = doc(db, "Enquiries", id);
      await updateDoc(enquiryDoc, { addressed: !currentStatus });

      setEnquiries((prevEnquiries) =>
        prevEnquiries.map((enquiry) =>
          enquiry.id === id
            ? { ...enquiry, addressed: !currentStatus }
            : enquiry,
        ),
      );
    } catch (error) {
      // console.error("Error updating addressed status:", error);
    }
  };
  const updateRemarks = async (id, remarks) => {
    const stateKey = `enquiry-${id}`;
    setNoteSaveStates((previous) => ({ ...previous, [stateKey]: "saving" }));
    try {
      const enquiryDoc = doc(db, "Enquiries", id);
      await updateDoc(enquiryDoc, { remarks });
      setEnquiries((prevEnquiries) =>
        prevEnquiries.map((enquiry) =>
          enquiry.id === id ? { ...enquiry, remarks } : enquiry,
        ),
      );
      setNoteSaveStates((previous) => ({ ...previous, [stateKey]: "saved" }));
    } catch (error) {
      setNoteSaveStates((previous) => ({ ...previous, [stateKey]: "error" }));
      // console.error("Error updating remarks:", error);
    }
  };

  const updateCheck2Remarks = async (id, remarks) => {
    const stateKey = `check2-${id}`;
    setNoteSaveStates((previous) => ({ ...previous, [stateKey]: "saving" }));
    try {
      const enquiryDoc = doc(db, "Compliance", "Check²", "Enquiries", id);
      await updateDoc(enquiryDoc, { remarks });

      setCheck2Enquiries((prev) =>
        prev.map((enq) => (enq.id === id ? { ...enq, remarks } : enq)),
      );
      setNoteSaveStates((previous) => ({ ...previous, [stateKey]: "saved" }));
    } catch (error) {
      setNoteSaveStates((previous) => ({ ...previous, [stateKey]: "error" }));
      // console.error("Error updating Check² remarks:", error);
    }
  };

  // const parseDate = (dateString) => {
  //   return dateString ? new Date(dateString) : new Date(9999, 11, 31);
  // };
  const parseDate = (dateString) => {
    if (!dateString) return null; // Ensure missing dates are handled
    const [day, month, year] = dateString.split("/").map(Number);
    return new Date(year, month - 1, day); // Convert "DD/MM/YYYY" to Date object
  };

  const filteredEnquiries = useMemo(() => {
    let filtered = [...brochureRequests];

    if (filter === "adviser") {
      filtered = filtered.filter((enquiry) => enquiry.role === "Adviser");
    } else if (filter === "customer") {
      filtered = filtered.filter((enquiry) => enquiry.role === "Customer");
    }

    return filtered.sort(
      (a, b) => parseDate(b.createdAt) - parseDate(a.createdAt),
    );
  }, [brochureRequests, filter]);

  const parseTime = (timeString) => {
    if (!timeString) {
      return 1440; // A large number for missing times (e.g., 24:00 in minutes)
    }

    const [startTime] = timeString.split("-"); // Extract the start time (e.g., "09:00" from "09:00-09:30")
    const [hours, minutes] = startTime.split(":").map(Number);
    return hours * 60 + minutes; // Convert to total minutes
  };

  const sortedEnquiries = useMemo(() => {
    return [...enquiries].sort((a, b) => {
      // 1. Sort by addressed status
      if (a.addressed !== b.addressed) {
        return a.addressed - b.addressed;
      }

      // 2. Sort by date (missing dates are placed last)
      const dateA = a.date ? parseDate(a.date) : new Date(9999, 11, 31);
      const dateB = b.date ? parseDate(b.date) : new Date(9999, 11, 31);

      if (dateA - dateB !== 0) {
        return dateA - dateB; // Earlier dates first
      }

      // 3. Sort by time (missing times are placed last)
      const timeA = parseTime(a.time);
      const timeB = parseTime(b.time);

      return timeA - timeB; // Earlier times first
    });
  }, [enquiries]);

  const filteredAndSortedEnquiries = useMemo(() => {
    let filtered = [...enquiries];

    // Apply filter
    if (filter === "adviser") {
      filtered = filtered.filter((enquiry) => enquiry.role === "Adviser");
    } else if (filter === "customer") {
      filtered = filtered.filter((enquiry) => enquiry.role === "Customer");
    }

    // Sorting Logic:
    return filtered.sort((a, b) => {
      const dateA = parseDate(a.createdAt);
      const dateB = parseDate(b.createdAt);

      // 1. Unaddressed enquiries should appear first
      if (a.addressed !== b.addressed) {
        return a.addressed ? 1 : -1;
      }

      // 2. If both entries have dates, sort in descending order (latest first)
      if (dateA && dateB) {
        return dateB - dateA;
      }

      // 3. Move entries with no date to the bottom
      if (!dateA) return 1;
      if (!dateB) return -1;

      return 0;
    });
  }, [enquiries, filter]);

  const sortedCheck2Enquiries = useMemo(() => {
    return [...check2Enquiries].sort((a, b) => {
      const dateA = parseDate(a.createdAt);
      const dateB = parseDate(b.createdAt);

      if (dateA && dateB) return dateB - dateA; // latest first
      if (!dateA) return 1;
      if (!dateB) return -1;
      return 0;
    });
  }, [check2Enquiries]);

  return (
    <div className="main-content">
      <Helmet>
        <title>Enquiries</title>
        <meta
          name="description"
          content="View and manage enquiries, including customer and adviser requests."
        />
      </Helmet>
      <Sidebar />
      <div className="enquiries-list-container">
        <Breadcrumb
          items={[
            {
              label: "Adviser Support",
              to: APP_ROUTES.DEPARTMENT_ADVISER_SUPPORT,
            },
            { label: "Enquiries" },
          ]}
        />
        <h3 className="FP_H3">Enquiries</h3>
        <div className="RadioButton-Container">
          <label className="Radio-Option">
            <input
              type="radio"
              value="all"
              checked={filter === "all"}
              onChange={() => setFilter("all")}
            />
            <span className="Custom-RadioButton"></span>
            <div className="FP_Content black">Show All</div>
          </label>
          <label className="Radio-Option">
            <input
              type="radio"
              value="adviser"
              checked={filter === "adviser"}
              onChange={() => setFilter("adviser")}
            />
            <span className="Custom-RadioButton"></span>
            <div className="FP_Content black">Adviser Enquiries</div>
          </label>
          <label className="Radio-Option">
            <input
              type="radio"
              value="customer"
              checked={filter === "customer"}
              onChange={() => setFilter("customer")}
            />
            <span className="Custom-RadioButton"></span>
            <div className="FP_Content black">Customer Enquiries</div>
          </label>
          <label className="Radio-Option">
            <input
              type="radio"
              value="brochure requests"
              checked={filter === "brochure requests"}
              onChange={() => setFilter("brochure requests")}
            />
            <span className="Custom-RadioButton"></span>
            <div className="FP_Content black">Brochure Requests</div>
          </label>
          {userPermissions.includes("Check ² Admin") && (
            <label className="Radio-Option">
              <input
                type="radio"
                value="check2"
                checked={filter === "check2"}
                onChange={() => setFilter("check2")}
              />
              <span className="Custom-RadioButton"></span>
              <div className="FP_Content black">check²</div>
            </label>
          )}
        </div>

        {/* <EnquiriesChart /> */}
        <div className="enquiries-table-scroll">
          {filter === "check2" ? (
            <table>
            <thead>
              <tr>
                <th>
                  <div className="FP_Help_Text_Semibold"></div>
                </th>
                <th>
                  <div className="FP_Help_Text_Semibold">Details</div>
                </th>
                <th>
                  <div
                    className="FP_Help_Text_Semibold"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Received On
                  </div>
                </th>
                <th>
                  <div className="FP_Help_Text_Semibold">Details</div>
                </th>
                <th style={{ width: "200px" }}>
                  <div className="FP_Help_Text_Semibold">Notes & Comments</div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedCheck2Enquiries.map((item, index) => (
                <tr key={item.id}>
                  <td>
                    <div className="FP_Content black">{index + 1}.</div>
                  </td>

                  <td>
                    <div className="Content-SemiBold">
                      {item.fullName || ""}
                    </div>
                    {item.email ? (
                      <a href={`mailto:${item.email}`}>
                        <div className="FP_Content black">{item.email}</div>
                      </a>
                    ) : (
                      ""
                    )}
                    <div className="vertical-spacer" />
                    {item.phone ? (
                      <a href={`tel:${item.phone}`}>
                        <div className="FP_Content black">{item.phone}</div>
                      </a>
                    ) : (
                      ""
                    )}
                  </td>
                  <td>
                    <div className="FP_Content black">{item.createdAt || ""}</div>
                  </td>

                  <td>
                    {item.companyName && (
                      <div className="FP_Content black">
                        <span className="Content-Small">Company Name: </span>
                        {item.companyName || ""}
                      </div>
                    )}
                    {item.sector &&
                      Array.isArray(item.sector) &&
                      item.sector.length > 0 && (
                        <div className="FP_Content black">
                          <span className="Content-Small">Industry: </span>
                          {item.sector.join(", ")}
                        </div>
                      )}
                    {item.message && (
                      <div className="FP_Content black">
                        <span className="Content-Small">Message: </span>
                        {item.message}
                      </div>
                    )}
                  </td>
                  <td>
                    <NotesEditor
                      value={item.remarks || ""}
                      saveState={noteSaveStates[`check2-${item.id}`]}
                      onChange={(newRemarks) => {
                        setNoteSaveStates((previous) => ({
                          ...previous,
                          [`check2-${item.id}`]: "idle",
                        }));
                        setCheck2Enquiries((prev) =>
                          prev.map((x) =>
                            x.id === item.id
                              ? { ...x, remarks: newRemarks }
                              : x,
                          ),
                        );
                      }}
                      onSave={() =>
                        updateCheck2Remarks(item.id, item.remarks || "")
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          //  keep your existing logic exactly as it is
          <>
            {filteredAndSortedEnquiries.length > 0 &&
            filter !== "brochure requests" ? (
              <table>
                <thead>
                  <tr>
                    <th>
                      <div className="FP_Help_Text_Semibold"></div>
                    </th>

                    <th>
                      <div className="FP_Help_Text_Semibold">Details</div>
                    </th>
                    <th>
                      <div className="FP_Help_Text_Semibold">Message</div>
                    </th>
                    <th>
                      <div className="FP_Help_Text_Semibold">Status</div>
                    </th>
                    <th>
                      <div className="FP_Help_Text_Semibold">Notes & Comments</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedEnquiries.map((enquiry, index) => (
                    <tr key={enquiry.id}>
                      <td>
                        <div className="FP_Content black">{index + 1}.</div>
                      </td>
                      <td>
                        <div className="Content-SemiBold">
                          {enquiry.name || ""}
                        </div>

                        {enquiry.email ? (
                          <a href={`mailto:${enquiry.email}`}>
                            <div className="FP_Content black">{enquiry.email}</div>
                          </a>
                        ) : (
                          ""
                        )}

                        {enquiry.phoneNumber ? (
                          <a href={`tel:${enquiry.phoneNumber}`}>
                            <div className="FP_Content black">{enquiry.phoneNumber}</div>
                          </a>
                        ) : (
                          ""
                        )}

                        <div className="FP_Content black">
                          <>
                            <div className="vertical-spacer" />
                            <div className="Content-Small">Role: </div>
                            <div className="FP_Content black">{enquiry.role || ""}</div>
                          </>
                          {enquiry.town ? (
                            <>
                              <div className="vertical-spacer" />
                              <div className="Content-Small">Town:</div>
                              <div className="FP_Content black">
                                {" "}
                                {enquiry.town || ""}
                              </div>
                            </>
                          ) : (
                            ""
                          )}
                          {enquiry.createdAt ? (
                            <>
                              <div className="vertical-spacer" />
                              <div className="Content-Small">Received On:</div>
                              <div className="FP_Content black">
                                {enquiry.createdAt || ""}
                              </div>
                            </>
                          ) : (
                            ""
                          )}
                          <div className="enquiry-detail-group">
                            <div className="Content-Small">
                              Scheduled Callback:
                            </div>
                            {enquiry.date && enquiry.time ? (
                              <div className="enquiry-detail-value">
                                <FaPhoneAlt className="callback-icon yes-icon" />
                                <span>
                                  {enquiry.date} at {enquiry.time}
                                </span>
                              </div>
                            ) : (
                              <div className="enquiry-detail-value">
                                <FaPhoneAlt className="callback-icon no-icon" />
                                <span>Not requested</span>
                              </div>
                            )}
                          </div>
                          <div className="enquiry-detail-group">
                            <div className="Content-Small">
                              Services of Interest:
                            </div>
                            <div className="enquiry-services-list">
                              {Array.isArray(enquiry.services) &&
                              enquiry.services.length > 0 ? (
                                enquiry.services.map((service) => (
                                  <span
                                    key={service}
                                    className="service-badge"
                                    style={{
                                      backgroundColor:
                                        serviceColors[service] || "#E0E0E0",
                                      borderColor:
                                        serviceColors[service] || "#B8B8B8",
                                    }}
                                  >
                                    <span className="Content-Small">
                                      {service}
                                    </span>
                                  </span>
                                ))
                              ) : (
                                <span className="FP_Content black">Not provided</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div
                          className="FP_Content black"
                          style={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {enquiry.message || ""}
                        </div>
                      </td>
                      <td>
                        <button
                          className={`icon-button ${
                            enquiry.addressed
                              ? "approved-background"
                              : "pending-background"
                          }`}
                          onClick={() =>
                            toggleAddressedStatus(enquiry.id, enquiry.addressed)
                          }
                        >
                          {enquiry.addressed ? (
                            <FaCheck className="approved-icon" />
                          ) : (
                            <FaQuestion className="pending-icon" />
                          )}
                        </button>
                      </td>
                      {/* <td>
                  <button onClick={() => handleAddToCalendar(enquiry)}>
                    Add to Calendar
                  </button>
                </td> */}
                      <td>
                        <NotesEditor
                          value={enquiry.remarks || ""}
                          saveState={noteSaveStates[`enquiry-${enquiry.id}`]}
                          onChange={(newRemarks) => {
                            setNoteSaveStates((previous) => ({
                              ...previous,
                              [`enquiry-${enquiry.id}`]: "idle",
                            }));
                            setEnquiries((prevEnquiries) =>
                              prevEnquiries.map((item) =>
                                item.id === enquiry.id
                                  ? { ...item, remarks: newRemarks }
                                  : item,
                              ),
                            );
                          }}
                          onSave={() =>
                            updateRemarks(enquiry.id, enquiry.remarks || "")
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              // <div className="FP_Content black">No enquiries found.</div>
              <table>
                <thead>
                  <tr>
                    <th>
                      <div className="FP_Help_Text_Semibold"></div>
                    </th>

                    <th>
                      <div className="FP_Help_Text_Semibold">Requested By</div>
                    </th>
                    <th>
                      <div className="FP_Help_Text_Semibold">Email Address</div>
                    </th>
                    <th>
                      <div className="FP_Help_Text_Semibold">Requested On</div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnquiries.map((request, index) => (
                    <tr key={request.id}>
                      <td>
                        <div className="FP_Content black">{index + 1}.</div>
                      </td>
                      <td>
                        <div className="Content-SemiBold">
                          {request.name || ""}
                        </div>
                      </td>
                      <td>
                        {request.email ? (
                          <a href={`mailto:${request.email}`}>
                            <div className="FP_Content black">{request.email}</div>
                          </a>
                        ) : (
                          ""
                        )}
                      </td>
                      <td>
                        <div className="FP_Content black">{request.createdAt || ""}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}{" "}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnquiriesList;
